import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Sparkles,
  Zap,
  Crown,
} from 'lucide-react';

const plans = [
  {
    name: 'Agent Lite',
    icon: Zap,
    price: 'Free',
    period: '7 Days',
    description: 'Try Agentick risk-free. Perfect to explore and test your AI agent.',
    highlight: false,
    badge: null,
    cta: 'Start Free Trial',
    ctaVariant: 'glass',
    features: [
      { label: 'AI Responses', value: '100 total', included: true },
      { label: 'WhatsApp Numbers', value: '1 Number', included: true },
      { label: 'Team Agents', value: '1 User (Admin)', included: true },
      { label: 'Knowledge Base', value: 'Up to 5 Docs', included: true },
      { label: 'Document Storage', value: '10 MB Total', included: true },
      { label: 'Standard Text Chat', value: null, included: true },
      { label: 'Human Handoff', value: null, included: true },
      { label: 'Audio Voice Notes AI', value: null, included: false },
      { label: 'Custom AI Persona/Tone', value: 'Default Only', included: false },
      { label: 'Automated CSAT Polls', value: null, included: false },
      { label: 'Analytics Dashboard', value: null, included: false },
      { label: 'API Access', value: null, included: false },
      { label: 'Priority Support', value: 'Community', included: false },
      { label: 'Custom Integrations', value: null, included: false },
    ],
  },
  {
    name: 'Agent Smart',
    icon: Sparkles,
    price: '₹99',
    period: '/ month',
    description: 'For growing businesses ready to automate customer conversations at scale.',
    highlight: true,
    badge: 'Most Popular',
    cta: 'Get Started',
    ctaVariant: 'premium',
    features: [
      { label: 'AI Responses', value: '1,000 / month', included: true },
      { label: 'WhatsApp Numbers', value: '1 Number', included: true },
      { label: 'Team Agents', value: 'Up to 3 Users', included: true },
      { label: 'Knowledge Base', value: 'Up to 20 Docs', included: true },
      { label: 'Document Storage', value: '100 MB Total', included: true },
      { label: 'Standard Text Chat', value: null, included: true },
      { label: 'Human Handoff', value: null, included: true },
      { label: 'Audio Voice Notes AI', value: null, included: true },
      { label: 'Custom AI Persona/Tone', value: null, included: true },
      { label: 'Automated CSAT Polls', value: null, included: true },
      { label: 'Analytics Dashboard', value: 'Basic', included: true },
      { label: 'API Access', value: null, included: false },
      { label: 'Priority Support', value: 'Email (48h)', included: true },
      { label: 'Custom Integrations', value: null, included: false },
    ],
  },
  {
    name: 'Agent Pro',
    icon: Crown,
    price: '₹999',
    period: '/ month',
    description: 'Full power for teams that need advanced AI, analytics, and integrations.',
    highlight: false,
    badge: 'Best Value',
    cta: 'Get Started',
    ctaVariant: 'outline-primary',
    features: [
      { label: 'AI Responses', value: '12,000 / month', included: true },
      { label: 'WhatsApp Numbers', value: '1 Number', included: true },
      { label: 'Team Agents', value: 'Up to 10 Users', included: true },
      { label: 'Knowledge Base', value: 'Up to 50 Docs', included: true },
      { label: 'Document Storage', value: '500 MB Total', included: true },
      { label: 'Standard Text Chat', value: null, included: true },
      { label: 'Human Handoff', value: null, included: true },
      { label: 'Audio Voice Notes AI', value: null, included: true },
      { label: 'Custom AI Persona/Tone', value: null, included: true },
      { label: 'Automated CSAT Polls', value: null, included: true },
      { label: 'Analytics Dashboard', value: 'Advanced', included: true },
      { label: 'API Access', value: null, included: true },
      { label: 'Priority Support', value: 'Priority (12h)', included: true },
      { label: 'Custom Integrations', value: null, included: true },
    ],
  },
];

export const Pricing = () => {
  const [ref, isVisible] = useScrollAnimation(0.05);
  const [expandedPlan, setExpandedPlan] = useState(null);

  const toggleExpand = (idx) => {
    setExpandedPlan(expandedPlan === idx ? null : idx);
  };

  return (
    <section id="pricing" ref={ref} className="py-24 lg:py-32 relative">
      {/* Background effects */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.04] blur-[140px]" />
      <div className="absolute bottom-1/4 right-0 w-[300px] h-[300px] rounded-full bg-accent/[0.03] blur-[80px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="accent" className="mb-4">
              Simple Pricing
            </Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight"
          >
            Plans That Grow{' '}
            <span className="gradient-text-primary">With You</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed"
          >
            Start free, upgrade when you're ready. No hidden fees, no surprises — just honest pricing for Indian SMBs.
          </motion.p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.15 }}
              className="relative"
            >
              {/* Highlight glow for popular plan */}
              {plan.highlight && (
                <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-primary/30 via-primary/10 to-transparent pointer-events-none" />
              )}

              <div
                className={`relative glass-card rounded-2xl flex flex-col h-full transition-all duration-500 ${
                  plan.highlight
                    ? 'border-primary/30 shadow-glow-primary'
                    : 'hover:border-primary/10'
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge
                      variant={plan.highlight ? 'premium' : 'glass'}
                      className="text-[10px] px-3 py-1 shadow-sm"
                    >
                      {plan.badge}
                    </Badge>
                  </div>
                )}

                {/* Plan header */}
                <div className="p-7 pb-0">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        plan.highlight ? 'bg-primary/15' : 'bg-muted'
                      }`}
                    >
                      <plan.icon
                        size={18}
                        className={plan.highlight ? 'text-primary' : 'text-muted-foreground'}
                      />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground">
                      {plan.name}
                    </h3>
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-3">
                    <span className="font-heading text-4xl lg:text-5xl font-bold text-foreground">
                      {plan.price}
                    </span>
                    <span className="text-sm text-muted-foreground font-body">
                      {plan.period}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground font-body leading-relaxed mb-6">
                    {plan.description}
                  </p>

                  {/* CTA */}
                  <Button
                    variant={plan.ctaVariant}
                    size="lg"
                    className="w-full mb-6"
                    asChild
                  >
                    <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">
                      {plan.cta}
                      <ArrowRight size={16} />
                    </a>
                  </Button>
                </div>

                {/* Divider */}
                <div className="mx-7 border-t border-border/50" />

                {/* Key features - always visible (first 7) */}
                <div className="p-7 pt-5 flex flex-col flex-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4 font-heading">
                    What's included
                  </p>
                  <ul className="space-y-3">
                    {plan.features.slice(0, 7).map((f) => (
                      <li key={f.label} className="flex items-start gap-2.5">
                        {f.included ? (
                          <CheckCircle2
                            size={16}
                            className="text-primary shrink-0 mt-0.5"
                          />
                        ) : (
                          <XCircle
                            size={16}
                            className="text-muted-foreground/40 shrink-0 mt-0.5"
                          />
                        )}
                        <span
                          className={`text-sm font-body leading-snug ${
                            f.included
                              ? 'text-secondary-foreground'
                              : 'text-muted-foreground/60'
                          }`}
                        >
                          {f.label}
                          {f.value && (
                            <span
                              className={`ml-1 ${
                                f.included ? 'text-foreground font-medium' : 'text-muted-foreground/50'
                              }`}
                            >
                              — {f.value}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Expandable remaining features */}
                  {plan.features.length > 7 && (
                    <>
                      <motion.div
                        initial={false}
                        animate={{
                          height: expandedPlan === i ? 'auto' : 0,
                          opacity: expandedPlan === i ? 1 : 0,
                        }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <ul className="space-y-3 pt-3">
                          {plan.features.slice(7).map((f) => (
                            <li key={f.label} className="flex items-start gap-2.5">
                              {f.included ? (
                                <CheckCircle2
                                  size={16}
                                  className="text-primary shrink-0 mt-0.5"
                                />
                              ) : (
                                <XCircle
                                  size={16}
                                  className="text-muted-foreground/40 shrink-0 mt-0.5"
                                />
                              )}
                              <span
                                className={`text-sm font-body leading-snug ${
                                  f.included
                                    ? 'text-secondary-foreground'
                                    : 'text-muted-foreground/60'
                                }`}
                              >
                                {f.label}
                                {f.value && (
                                  <span
                                    className={`ml-1 ${
                                      f.included ? 'text-foreground font-medium' : 'text-muted-foreground/50'
                                    }`}
                                  >
                                    — {f.value}
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>

                      <button
                        onClick={() => toggleExpand(i)}
                        className="mt-4 text-xs font-medium text-primary hover:text-primary-glow transition-colors duration-200 font-body text-left"
                      >
                        {expandedPlan === i ? 'Show less' : `Show ${plan.features.length - 7} more features`}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="text-center text-sm text-muted-foreground font-body mt-10"
        >
          All plans include WhatsApp integration without Business API. Upgrade, downgrade, or cancel anytime.
        </motion.p>
      </div>
    </section>
  );
};
